<script lang="ts" setup>
import { GoogleMap, Marker, InfoWindow } from "vue3-google-map";
import { ref, computed, watch, onMounted, inject} from "vue";
import { useGeolocation, } from "@vueuse/core";
import { useForm } from "@/helpers/composables/useForm";
import { Head } from "@inertiajs/vue3";
import { string, object, number } from "yup";
import RothcoCta from "@/components/RothcoCta.vue";
import Recaptcha from "@/components/Recaptcha.vue";
import { useSettingsStore } from "@/store/settings";
import { storeToRefs } from "pinia";
import { watchDebounced } from "@vueuse/core";
import ErrorIcon from "@icons/error.svg";
import useGoogleManager from "@/helpers/composables/google";
let route = inject("route");

const settings = useSettingsStore();

const { user } = storeToRefs(settings);

let hasParams = (params)=>{
    let fields = ['distance', 'market_type', 'zip']
    return fields.some((field)=>{
        return params[field] != null;
    });
}
let params = computed(()=>{
    let params = {
        distance: 25,
        market_type: 0,
        address: null
    };
    if(hasParams(route().params)){
        params = {
            ...params,
            ...route().params
        };
    }
    return params;
});


const { coords } = useGeolocation();

let googleMapRef = ref();

let coordinates = computed(()=>{
    if(enteredCoordinates.value.lat && enteredCoordinates.value.lng){
        return {
            lat: enteredCoordinates.value.lat,
            lng: enteredCoordinates.value.lng
        }
    }

    return {
        lat: coords.value.latitude == Infinity ? 0 : coords.value.latitude,
        lng: coords.value.longitude == Infinity ? 0 : coords.value.longitude
    }
});

let geoCoder = computed(()=>{
    if(!googleMapRef.value){
        return {
            geocode: (params, callBack)=>{
                let interval = setInterval(()=>{
                    if(googleMapRef.value){
                        clearInterval(interval);
                        return new googleMapRef.value.api.Geocoder().geocode(params, callBack);
                    }
                }, 100);
            }
        };
    }
    return new googleMapRef.value.api.Geocoder();
});


let enteredCoordinates = ref({
    lat: null,
    lng: null
});

let address = ref(params.value.address);

if(address.value){
    geoCoder.value.geocode({address: address.value}, (results, status)=>{
        if(status === "OK"){
            enteredCoordinates.value.lat = results[0].geometry.location.lat();
            enteredCoordinates.value.lng = results[0].geometry.location.lng();
        }
    });
}

function triggerLatLongLookup(){
    let latlng = new googleMapRef.value.api.LatLng(coordinates.value.lat, coordinates.value.lng);
    geoCoder.value.geocode({location: latlng}, (results, status)=>{
        if(status === "OK"){
            form.address = results[0].address_components.find((component)=>{
                return component.types.includes("postal_code");
            })?.long_name;

            if(form.address == null){
                form.setError({
                    address: "Invalid address"
                });
            }else{
                form.setError({
                    address: null
                });
            }
        }
    });
}
watch(() => coordinates, (newVal)=>{
    if(newVal.value.lat && newVal.value.lng && googleMapRef.value && form.zip == null){
        let latlng = new googleMapRef.value.api.LatLng(newVal.value.lat, newVal.value.lng);
        geoCoder.value.geocode({location: latlng}, (results, status)=>{
            if(status === "OK"){
                if(address.value == null || address.value == ""){
                    address.value = results[0].address_components.find((component)=>{
                        return component.types.includes("postal_code");
                    })?.long_name;

                    form.zip = results[0].address_components.find((component)=>{
                        return component.types.includes("postal_code");
                    })?.long_name;

                    if(address.value == null){
                        form.setError({
                            address: "Invalid address"
                        });
                    }else{
                        form.setError({
                            address: null
                        });
                    }
                }
            }
        });
    }
}, {deep: true, immediate: true});

onMounted(()=>{
    for(let key in params.value){
        form[key] = params.value[key];
    }
})

watchDebounced(coordinates, (newVal)=>{
    if(newVal.lat && newVal.lng && googleMapRef.value && form.address == null){
        triggerLatLongLookup();
    }
}, {deep: true, immediate: true});



let props = defineProps({
    markets: {
        type: Object,
        default(){
            return {};
        }
    },
    dealers: {
        type: Array,
        default(){
            return [];
        }
    },
    errors: {
        type: Object,
        default(){
            return {};
        }
    }
})



let form = useForm({
    useInertia: true,
    inertiaAlias: "RetailLocator",
    schema: object({
        address: string().required("Address is required").default(params.value.zip),
        market_type: string().required().default(params.value.market_type),
        distance: number()
            .transform((value, originalValue) => originalValue === "" ? NaN : value)
            .typeError("Distance is required")
            .required("Distance is required")
            .default(params.value.distance ?? 25),
        lat: number(),
        lng: number(),
        recaptcha: string().notRequired().default(null),
    }),
})

let captchaEntered = computed(()=>{
    if(user.value.user_id == null){
        return form.recaptcha != null;
    }
    return true;
})

let dealerMarkers = computed(()=>{
    // Inertia may pass null when session has no dealers (Vue defaults only apply for undefined)
    return (props.dealers ?? []).map((dealer)=>{
        return {
            position: {
                lat: +dealer.latitude,
                lng: +dealer.longitude
            },
            title: dealer.company_name,
            address: `${dealer.address_a}, ${dealer.city}, ${dealer.state} ${dealer.zip}`,
            phone: dealer.phone1
        }
    });
});

let visibleInfoWindow = ref(null);

let handleMarkerClick = (marker)=>{
    visibleInfoWindow.value = marker;
}

let handleInfoWindowClose = ()=>{
    visibleInfoWindow.value = null;
}

const zoomLevel = computed(()=>{
  if (form.distance > 1000) return 4;
  if (form.distance > 500) return 5;
  if (form.distance > 250) return 6;
  if (form.distance > 100) return 7;
  if (form.distance > 50) return 8;
  if (form.distance > 25) return 9;
  if (form.distance > 10) return 10;
  return 11; // Fine-tuned zoom for smaller distances
})

let errs = computed(()=>{
    let errors = Object.values(props.errors);

    let mergedErrors = [...new Set([...errors])];
    return mergedErrors;

})

 
let marketTypes = computed(()=>{
    return Object.keys(props.markets).map((key)=>{
        return {
            id: key,
            key: key,
            value: props.markets[key]
        }
    });
})
let marketType = computed({
    get(){
        return marketTypes.value.find((market)=>{
            return market.id == form.market_type;
        }) ?? marketTypes.value[0];
    },
    set(value){
        form.market_type = value.key;
    }
})

let { gtmRetailLocatorEvent } = useGoogleManager();

let submitForm = async () => {
    if(form.processing) return;

    await form.validate({all: true});
    if(Object.keys(form.errors).length === 0){
        gtmRetailLocatorEvent({
            latitude: coordinates.value.lat,
            longitude: coordinates.value.lng,
            zip_code: form.zip,
            distance: form.distance,
            market_type: form.market_type,
        })
        form.transform(data => {
            return {
                address: data.address,
                market_type: data.market_type,
                distance: data.distance,
                'g-recaptcha-response': data.recaptcha,
            }
        }).post(route('retail.locator.search.zip'), {
            preserveScroll: true,
            preserveState: false,
            onFinish: () => {
                form.recaptcha = null;
            },
        });
    }
};


const fitMapToMarkers = () => {
    const map = googleMapRef.value?.map
    const markers = dealerMarkers.value ?? []
    if (!map || markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    markers.forEach(dealer => {
        if (dealer.position?.lat && dealer.position?.lng) {
            const latLng = new google.maps.LatLng(dealer.position.lat, dealer.position.lng)
            bounds.extend(latLng)
        }
    })

    map.fitBounds(bounds, 20)
}

let canonicalUrl = computed(() => {
    return route('retail.locator.search');
});
</script>   
<template>

    <div>
    <Head>
        <title>Rothco Military and Tactical Gear Retail Locator</title>
        <meta name="Since 1953, Rothco has supplied top retailers worldwide with trusted military, tactical, work, and outdoor gear built for performance and durability." />
        <meta name="keywords" content="Rothco store, Rothco retailer, Dealer locator, store finder, Rothco, tactical stores, military stores, outdoor stores, independent retails, Rothco gear, Rothco goods for sales, Army Navy Store, Military surplus store" />
        <link rel="canonical" :href="canonicalUrl" />
    </Head>
    <div>
        <h2 class="headline-xl">Retail Locator</h2>
        
        <div class="form-field-container">
            <div v-for="(error, index) of errs" class="notification notification-error" :key="index">
                <ErrorIcon alt="Error!" />
                <p class="paragraph-details"><strong>Error Notification</strong>: {{ error }}</p>
            </div>
            <custom-select id="market-type" v-model="marketType" :options="marketTypes" label="Market Type"/>
            <div class="input-folder">
                <div class="input-folder--input">
                    <PrimeInputText placeholder="" id="address" v-model="form.address" :class="{'invalid-state-input': form.errors.address}"/>
                    <label for="address">
                        <p class="paragraph-default">
                            Address/Zip/Postal Code*
                        </p>
                    </label>
                </div>
                <div class="input-folder--support">
                    <p class="input-suport-text invalid-state" v-if="form.errors.address"> {{ form.errors.address}}</p>
                </div>
            </div>
            

            <div class="input-folder">
                <div class="input-folder--input">
                    <PrimeInputText placeholder="" id="distance" v-model.lazy="form.distance" :class="{'invalid-state-input': form.errors.distance}"/>
                    <label for="distance">
                        <p class="paragraph-default">
                            Distance (miles)*
                        </p>
                    </label>
                </div>
                <div class="input-folder--support">
                    <p class="input-suport-text invalid-state" v-if="form.errors.distance"> {{ form.errors.distance}}</p>
                </div>
            </div> 

            <Recaptcha v-if="user.user_id == null" v-model="form.recaptcha" theme="light"/>

            <div class="flex-row justify-end">
                <RothcoCta @click="submitForm" ctaType="submit" :class="{
                    primary: captchaEntered,
                    disabled: !captchaEntered
                }" :disabled="!captchaEntered || form.processing">
                    Find Retailers
                </RothcoCta>
            </div>
        </div>

        <section class="retail-locator-container">
            <div class="retailers">
                <div @click="handleMarkerClick(dealer)" :class="{
                    'retailer-card': true,
                    active: dealer.title == visibleInfoWindow?.title
                }" v-for="dealer of dealerMarkers" :key="dealer.title">
                    <p class="title-s">{{ dealer.title }}</p>
                    <p class="paragraph-default">{{ dealer.address }}<br /> <a v-if="dealer.phone" :href="`tel:${dealer.phone}`">{{ dealer.phone }}</a></p>    
                </div>    
            </div>
            <GoogleMap
                apiKey="AIzaSyBoaN5zuH5rqYCks9vMgbq-UngjJDofUEI"
                :center="coordinates"
                :zoom="zoomLevel"
                style="height: 600px; width: 100%"
                ref="googleMapRef"
                @tilesloaded="fitMapToMarkers"
            >
                <Marker @click="handleMarkerClick(dealer)" v-for="dealer of dealerMarkers" :key="dealer.title" :options="dealer" />
                <InfoWindow id="infoWindow" v-if="visibleInfoWindow" :options="{position: visibleInfoWindow.position}" @closeclick="handleInfoWindowClose">
                    <div>
                        <h3>{{ visibleInfoWindow.title }}</h3>
                        <p>{{ visibleInfoWindow.address }}</p>
                        <p>{{ visibleInfoWindow.phone }}</p>
                    </div>
                </InfoWindow>
            </GoogleMap>
            
        </section>  
    </div>
    </div>
</template>